import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { SplitRow } from '../ui';

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  & > * {
    min-width: 0;
  }
`;

export const SectionHeaderMb = styled(SectionHeader)`
  margin-bottom: 12px;
`;

export const SectionTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.01em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SectionSubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
`;

export const SectionLink = styled(Link)`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const AlertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(310px, 100%), 1fr));
  gap: 12px;
`;

export const AlertCard = styled(Link)<{ $severity: string }>`
  min-width: 0;
  overflow-wrap: anywhere;
  text-decoration: none;
  color: inherit;
  padding: 14px 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid
    ${({ $severity, theme }) =>
      $severity === 'critical'
        ? 'rgba(239, 68, 68, 0.55)'
        : $severity === 'warning'
          ? 'rgba(245, 158, 11, 0.5)'
          : theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 6px;
  transition: transform 140ms ease;
  &:hover {
    transform: translateY(-2px);
  }
`;

export const AlertTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const AlertArrow = styled.span`
  font-size: 11px;
  opacity: 0.7;
`;

export const AlertTitle = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const AlertDesc = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const AccountsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(260px, 100%), 1fr));
  gap: 14px;
`;

export const AccountCardBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const AccNumber = styled.div`
  font-size: 12px;
  font-family: monospace;
  color: ${({ theme }) => theme.colors.text.muted};
  overflow-wrap: anywhere;
`;

export const BalanceBig = styled.div`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow-wrap: anywhere;
`;

export const SubRow = styled(SplitRow)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const BlockedAmountText = styled.span`
  color: #f59e0b;
`;

export const AccFooter = styled.div`
  margin-top: 4px;
  font-size: 11px;
  opacity: 0.72;
  overflow-wrap: anywhere;
`;

export const TwoColGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(460px, 100%), 1fr));
  gap: 16px;

  & > * {
    min-width: 0;
  }
`;

export const ItemRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 8px 10px;
  font-size: 12px;
`;

export const ItemInfo = styled.div`
  flex: 1 1 200px;
  min-width: 0;
  overflow-wrap: anywhere;
`;

export const ItemSide = styled.div`
  margin-left: auto;
`;

export const PaymentRow = styled(ItemRow)`
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(148, 163, 184, 0.08);
`;

export const PaymentAmount = styled(ItemSide)<{ $inflow: boolean }>`
  font-weight: 800;
  white-space: nowrap;
  color: ${({ $inflow }) => ($inflow ? '#10b981' : '#ef4444')};
`;

export const InstrumentRow = styled(ItemRow)`
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid rgba(148, 163, 184, 0.22);
`;

export const StatTiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(120px, 100%), 1fr));
  gap: 8px;
  margin-bottom: 14px;
`;

export const StatTile = styled.div`
  padding: 10px;
  border-radius: 8px;
  background: rgba(148, 163, 184, 0.08);
  font-size: 12px;
`;

export const StatTileSuccess = styled.div`
  padding: 10px;
  border-radius: 8px;
  background: rgba(16, 185, 129, 0.1);
  font-size: 12px;
`;

export const TileLabel = styled.div`
  opacity: 0.7;
`;

export const TileValue = styled.div`
  font-size: 15px;
  font-weight: 800;
  margin-top: 2px;
`;

export const TileValueSuccess = styled.div`
  font-size: 15px;
  font-weight: 800;
  margin-top: 2px;
  color: #10b981;
`;

export const BarTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.backgroundElevated};
  overflow: hidden;
`;

export const BarFill = styled.div<{ $pct: number; $color?: string }>`
  height: 100%;
  width: ${({ $pct }) => `${Math.min(100, Math.max(2, $pct))}%`};
  background: ${({ $color, theme }) => $color ?? theme.colors.primary};
  border-radius: 999px;
`;

export const FunnelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  @media (max-width: 960px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (max-width: 480px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const FunnelCard = styled.div<{ $highlight?: boolean }>`
  min-width: 0;
  overflow-wrap: anywhere;
  padding: 16px;
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme, $highlight }) =>
    $highlight ? 'rgba(16, 185, 129, 0.08)' : theme.colors.backgroundElevated};
  border: 1px solid
    ${({ theme, $highlight }) =>
      $highlight ? 'rgba(16, 185, 129, 0.4)' : theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FunnelCardLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  opacity: 0.7;
`;

export const FunnelCardValue = styled.div`
  font-size: 22px;
  font-weight: 800;
`;

export const FunnelCardSubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
`;

export const FunnelSuccessValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #10b981;
`;

export const FunnelWarningValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: #f59e0b;
`;

export const OverdueHighlight = styled.strong<{ $hasOverdue?: boolean }>`
  color: ${({ $hasOverdue }) => ($hasOverdue ? '#ef4444' : 'inherit')};
`;

export const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 11px 14px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

export const CardSubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
`;

export const InflowBold = styled.span`
  color: #10b981;
  font-weight: 700;
`;

export const OutflowBold = styled.span`
  color: #ef4444;
  font-weight: 700;
`;

export const CategoryBreakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 14px;
`;

export const BreakdownTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  opacity: 0.85;
`;

export const CategoryRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const TopOperationsTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  margin-bottom: 8px;
  opacity: 0.85;
`;

export const OperationsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const CounterpartyText = styled.div`
  font-weight: 700;
`;

export const PurposeEllipsis = styled.div`
  opacity: 0.75;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 380px;
`;

export const InstrumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const InstrumentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

export const ContractNumberSpan = styled.span`
  opacity: 0.7;
`;

export const InstrumentTitle = styled.div`
  margin-top: 4px;
  opacity: 0.85;
`;

export const InstrumentDetails = styled.div`
  margin-top: 4px;
  opacity: 0.7;
`;

export const ProjectNameText = styled.div`
  font-weight: 700;
`;

export const CustomerNameText = styled.div`
  font-size: 12px;
  opacity: 0.75;
`;

export const DocNumberText = styled.div`
  font-size: 12px;
  opacity: 0.75;
`;

export const ReceivedCell = styled.td<{ $hasReceived?: boolean }>`
  color: ${({ $hasReceived }) => ($hasReceived ? '#10b981' : 'inherit')};
`;

export const ReceivableBalanceCell = styled.td`
  font-weight: 700;
`;
